import React, { useContext, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { StarIcon } from "@heroicons/react/24/solid";
import { ArrowRightIcon, HeartIcon } from "@heroicons/react/24/outline";
import { PackagesContext } from "../context/PackagesContext";
import { ThemeContext } from "../context/ThemeContext"; // ✅ ThemeContext import
import EnquiryPopup from "./EnquiryPopup";

const PackagesCards = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const destinationParam = searchParams.get("country");

  const { packages, loading } = useContext(PackagesContext);
  const { theme } = useContext(ThemeContext); // ✅ Use theme

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const handleTrip = (pkg) => {
    navigate(`/packages-description?destination=${pkg.id}&country=${pkg.country}`);
    window.scrollTo(0, 0);
  };

  const smoothScroll = (e) => {
    e.preventDefault();
    setIsPopupOpen(true);
  };

  const defaultPackages = [
    {
      img: "category-1.jpg",
      title: "Pattaya & Bangkok Special (4 Nights)",
      price: "Rs. 11,500",
      duration: "4 Nights 5 Days",
      stars: 4,
      reviews: 28,
    },
    // ... repeat or generate more mock items as needed
  ];

  const filteredPackages = destinationParam
    ? packages.filter(pkg =>
        pkg.country?.toLowerCase() === destinationParam.toLowerCase()
      )
    : packages;

  const displayedPackages = filteredPackages.length
    ? filteredPackages.map((pkg) => ({
        img: pkg.image2 || "https://via.placeholder.com/400x300",
        title: pkg.packageName,
        country: pkg.country,
        price: `INR ${pkg.price}`,
        duration: `${pkg.numberOfNights} Nights ${pkg.numberOfDays} Days`,
        reviews: Math.floor(Math.random() * 100) + 10,
        destination: pkg.destination,
        id: pkg.id,
      }))
    : defaultPackages;

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section
      className={`${
        theme === "dark" ? "dark" : ""
      } py-12 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900`}
      id="package"
    >
      <EnquiryPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />

      <div className="max-w-7xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center text-4xl font-bold text-gray-900 dark:text-white mb-12"
        >
          {destinationParam
            ? `Explore ${destinationParam} Travel Packages`
            : "Explore Our Travel Packages"}
        </motion.h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayedPackages.map((pkg, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: index * 0.1, duration: 0.3 }}
              className="group relative bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100 dark:border-gray-700"
            >
              <div className="absolute top-3 right-3 z-10">
                <button className="p-1.5 bg-white/90 dark:bg-gray-700 rounded-full shadow-xs hover:bg-red-50 dark:hover:bg-red-600/20 transition">
                  <HeartIcon className="w-5 h-5 text-gray-500 dark:text-white hover:text-red-500" />
                </button>
              </div>

              <div className="relative overflow-hidden rounded-t-lg">
                <motion.img
                  whileHover={{ scale: 1.03 }}
                  src={pkg.img}
                  alt={pkg.title}
                  className="w-full h-40 object-cover"
                />
                <div className="absolute bottom-2 bg-gradient-to-r from-purple-800 to-indigo-600 text-white px-4 py-2 rounded-r-full shadow-md backdrop-blur-sm bg-opacity-90 hover:shadow-indigo-400/40 transition-all duration-300">
                  <span className="text-sm font-semibold tracking-wide flex items-center gap-1">
                    {pkg.price}
                  </span>
                </div>
              </div>

              <div className="p-4">
                <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1 leading-tight">
                  {pkg.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-300 mb-3">
                  {pkg.duration}
                </p>

                <div className="flex gap-2 items-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{ backgroundColor: "#007bff" }}
                    className="flex items-center justify-center gap-1 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:shadow-md transition-all"
                    onClick={() => handleTrip(pkg)}
                  >
                    Trip Details
                  </motion.button>

                  <motion.button
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={smoothScroll}
                    className="px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-md text-sm font-medium hover:border-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Get Free Quote
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

PackagesCards.propTypes = {
  packages: PropTypes.arrayOf(
    PropTypes.shape({
      img: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      price: PropTypes.string.isRequired,
      duration: PropTypes.string.isRequired,
      reviews: PropTypes.number,
    })
  ),
};

export default PackagesCards;
